import { Component, Input, Output, EventEmitter, inject, OnInit, Pipe, PipeTransform, OnChanges } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { FormBuilder, Validators, AbstractControl, ValidationErrors, ValidatorFn, FormArray, FormGroup } from '@angular/forms';
import { RmsModule } from '@rms';
import { InboundTallyData } from '@interfaces';
import { ExcelWriterService, WebService, lastValueFrom, RmsMessageService } from '@services';
import { MatProgressSpinner, MatProgressSpinnerModule  } from '@angular/material/progress-spinner'

function isFiniteNumber(): ValidatorFn {
  return (ctrl: AbstractControl): ValidationErrors | null => {
    const v = ctrl.value;
    if (v === null || v === undefined || v === '') return { required: true };
    const num = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(num) ? null : { notNumber: true };
  };
}

function isInteger(): ValidatorFn {
  return (ctrl: AbstractControl): ValidationErrors | null => {
    const v = ctrl.value;
    if (v === null || v === undefined || v === '') return { required: true };
    const num = typeof v === 'number' ? v : Number(v);
    return Number.isInteger(num) ? null : { notInteger: true };
  };
}

function arrayMinLength(min: number): ValidatorFn {
  return (ctrl: AbstractControl): ValidationErrors | null => {
    if (!(ctrl instanceof FormArray)) return null;
    return ctrl.length >= min ? null : { minItems: { min, actual: ctrl.length } };
  };
}

function arrayMaxLength(max: number): ValidatorFn {
  return (ctrl: AbstractControl): ValidationErrors | null => {
    if (!(ctrl instanceof FormArray)) return null;
    return ctrl.length <= max ? null : { maxItems: { max, actual: ctrl.length } };
  };
}

function minCompletedRows(min: number, fields: string[]): ValidatorFn {
  return (ctrl: AbstractControl): ValidationErrors | null => {
    if (!(ctrl instanceof FormArray)) return null;
    const completed = ctrl.controls.filter(fg =>
      fields.every(f => {
        const c = (fg as FormGroup).get(f);
        return c && c.valid && c.value !== null && c.value !== '';
      })
    ).length;
    return completed >= min ? null : { minCompleted: { min, actual: completed } };
  };
}
@Pipe({ name: 'safeUrl' })
export class SafeUrlPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) { }
  transform(value: string) {
    return this.sanitizer.bypassSecurityTrustResourceUrl(value);
  }
}

@Component({
  selector: 'app-tally-creator',
  imports: [RmsModule, MatProgressSpinnerModule, SafeUrlPipe],
  templateUrl: './tally-creator.component.html',
  styleUrl: './tally-creator.component.scss'
})
export class TallyCreatorComponent implements OnInit, OnChanges {
  @Input() InventoryItem: any;
  @Input() existingTally?: InboundTallyData; 
  @Output() closeTallyCreator = new EventEmitter<void>();

  tallyData: InboundTallyData | null = null;
  readonly MAX_PALLETS = 22;
  readonly TARE_COUNT = 3;
  readonly SPOT_COUNT = 6;
  private _formBuilder = inject(FormBuilder);
  confirmationReady = false;
  tallyPreviewUrl: string | null = null;
  Uploaded: boolean = false;
  constructor(private excel: ExcelWriterService, private web: WebService, private msg: RmsMessageService) { }

  ngOnChanges(): void {
    if (this.existingTally) {
      this.hydrateFromData(this.existingTally);
    }
  }

  ngOnInit(): void {

    if (this.existingTally) {
      this.hydrateFromData(this.existingTally);
      return
    }

    if (this.pallets.length === 0) {
      this.addPallet();
    }

    // Initialize TARE rows (exactly 3, all required)
    if (this.tareWeights.length === 0) {
      for (let i = 0; i < this.TARE_COUNT; i++) {
        this.tareWeights.push(this.createTareRow(i + 1)); // prefill bag #
      }
    }

    // Initialize SPOT rows (always render 6, require at least 3 completed)
    if (this.spotChecks.length === 0) {
      for (let i = 0; i < this.SPOT_COUNT; i++) {
        this.spotChecks.push(this.createSpotRow(i + 1)); // prefill bag #
      }
    }

  }

  palletFormGroup = this._formBuilder.group({
    pallets: this._formBuilder.array<FormGroup>([], [
      arrayMinLength(1),
      arrayMaxLength(this.MAX_PALLETS),
    ]),
    slacks: this._formBuilder.array<FormGroup>([], [
      arrayMinLength(0),
      arrayMaxLength(6),
    ]),
    palletWeight: ['', [isFiniteNumber(), Validators.min(0)]],
  });


  unloadingFormGroup = this._formBuilder.group({
    hiloDriver: ['', Validators.required],
    helper1: ['', Validators.required],
    helper2: [''],
    stackingDriver: ['', Validators.required],
    LocationNumber: [''],
    Comments: [''],
    DateStripped: [new Date().toISOString().substring(0, 10), Validators.required],

    tareWeights: this._formBuilder.array<FormGroup>(
      [], // we’ll populate in ngOnInit
      []  // per-row required validators are enough
    ),

    spotChecks: this._formBuilder.array<FormGroup>(
      [], // populate in ngOnInit
      [minCompletedRows(3, ['TestWeight'])] // <- need at least 3 completed
    ),
    condition: this._formBuilder.group({
      condensation: [false],
      paperLining: [false],
      holeInContainer: [false],
      other: [false],
    }),
    insects: this._formBuilder.group({
      larvae: [false],
      beetles: [false],
      moths: [false],
      other: [false],
    })
  });

  private createPalletRow(): FormGroup {
    return this._formBuilder.group({
      WeightWithPallet: [
        '', [Validators.required, isFiniteNumber(), Validators.min(0)]
      ],
      Bags: [
        '', [Validators.required, isInteger(), Validators.min(0)]
      ],
    });
  }
  private createSlackRow(): FormGroup {
    return this._formBuilder.group({
      GrossWeight: ['', [Validators.required, isFiniteNumber(), Validators.min(0)]],
      Bags: ['', [Validators.required, isInteger(), Validators.min(0)]],
    })
  }

  private createTareRow(prefillBag?: number): FormGroup {
    return this._formBuilder.group({
      BagNumber: [{value: prefillBag ?? '', disabled: true}],
      TareWeight: ['', [Validators.required, isFiniteNumber(), Validators.min(0)]],
    });
  }

  private createSpotRow(prefillBag?: number): FormGroup {
    return this._formBuilder.group({
      BagNumber: [{ value: prefillBag ?? '', disabled: true}], // optional—completeness checked by array validator
      TestWeight: ['', [isFiniteNumber(), Validators.min(0)]],           // optional—completeness checked by array validator
    });
  }

  get pallets(): FormArray<FormGroup> {
    return this.palletFormGroup.get('pallets') as FormArray<FormGroup>;
  }
  get slacks(): FormArray<FormGroup> {
    return this.palletFormGroup.get('slacks') as FormArray<FormGroup>;
  }
  get tareWeights(): FormArray<FormGroup> {
    return this.unloadingFormGroup.get('tareWeights') as FormArray<FormGroup>;
  }
  get spotChecks(): FormArray<FormGroup> {
    return this.unloadingFormGroup.get('spotChecks') as FormArray<FormGroup>;
  }

  /* -- Mutators -- */
  addPallet(): void {
    if (this.pallets.length >= this.MAX_PALLETS) return;
    this.pallets.push(this.createPalletRow());
  }
  addSlack(): void {
    if (this.slacks.length >= 6) return;
    this.slacks.push(this.createSlackRow());
  }
  removePallet(index: number): void {
    if (index < 0 || index >= this.pallets.length) return;
    this.pallets.removeAt(index);
  }
  removeSlack(index: number): void {
    if (index < 0 || index >= this.slacks.length) return;
    this.slacks.removeAt(index);
  }

  /* -- Helpers for UI -- */
  canAddMore(): boolean {
    return this.pallets.length < this.MAX_PALLETS;
  }
  canAddMoreSlacks(): boolean {
    return this.slacks.length < 6;
  }
  markCurrentStepTouched(): void {
    this.palletFormGroup.markAllAsTouched();
  }

  onStepChange(event: any) {
    console.log(event)
    if (event.selectedIndex === 3) { // confirmation step index
      this.generateTallySheet();
    }
    if (event.selectedIndex === 4) {
      this.finish();
    }
  }

  async generateTallySheet(): Promise<void> {
    try {
      this.confirmationReady = false;
      this.tallyPreviewUrl = null;

      const data = this.buildInboundTallyData();

      // Writer returns string URL when `getPreview=true`
      const fileBase = (data.RefPO || 'Tally').replace(/[^\w\-]+/g, '_');
      const urlOrBytes = await this.excel.FillInboundTallyTemplate(data, true);

      if (typeof urlOrBytes === 'string') {

        const encoded = encodeURIComponent(urlOrBytes);
        this.tallyPreviewUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encoded}`

      } else {
        // Fallback (shouldn’t happen when getPreview=true), you could upload here
        console.warn('Expected preview URL string, got bytes buffer instead.');
      }

      this.confirmationReady = true;
    } catch (e) {
      console.error('Tally generation failed', e);
      this.confirmationReady = true;
    }
  }
  async finish() {
    try {
      const data = this.buildInboundTallyData();
      const urlOrBytes = await this.excel.FillInboundTallyTemplate(data, false);

      const bytes = urlOrBytes as Uint8Array;
      const fileBlob = new Blob([bytes], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      

      const form = new FormData();
      form.append('file', fileBlob, `${data.RefPO || 'Tally'}.xlsx`);
      form.append('fileName', `PO#${this.InventoryItem.PurchaseOrderID} - Reff#${this.InventoryItem.InventoryID} - Tally Sheet.xlsx`);
      form.append('path', `purchase-orders/po-${this.InventoryItem.PurchaseOrderID}/reff-${this.InventoryItem.InventoryID}`,)
      await lastValueFrom(this.web.getWebCall('files', '/upload', { body: form }))
      this.msg.success('Tally sheet uploaded successfully.');
      this.Uploaded = true;
    }
    catch (error) {
      console.error('Error uploading tally sheet:', error);
    }
  }

  private mapConditionsToArray(group: FormGroup): InboundTallyData['ConditionOnArrival'] {
    const g = group.getRawValue() as { condensation: boolean; paperLining: boolean; holeInContainer: boolean; other: boolean; };
    const out: InboundTallyData['ConditionOnArrival'] = [];
    if (g.condensation) out.push('Condensation');
    if (g.paperLining) out.push('Paper Lining');        // NOTE: your union type uses 'Paper Lining' or 'Paper Lining'? In service I see 'Paper Lining' in labels but union is 'Paper Lining' vs 'Paper Lining'? If your union literal is 'Paper Lining' keep it exactly.
    if (g.holeInContainer) out.push('HoleInContainer'); // match union literal
    if (g.other) out.push('Other');
    return out;
  }

  private mapInsectsToArray(group: FormGroup): InboundTallyData['InsectActivity'] {
    const g = group.getRawValue() as { larvae: boolean; beetles: boolean; moths: boolean; other: boolean; };
    const out: InboundTallyData['InsectActivity'] = [];
    if (g.larvae) out.push('Larvae');
    if (g.beetles) out.push('Beetles');
    if (g.moths) out.push('Moths');
    if (g.other) out.push('Other');
    return out;
  }

  /** Build the exact InboundTallyData object expected by ExcelWriterService */
  private buildInboundTallyData(): InboundTallyData {
    const palletsRaw = this.palletFormGroup.getRawValue();
    const unloadRaw = this.unloadingFormGroup.getRawValue();

    // Map Full Pallet Weights (form → service’s misspelled property 'WeightWithPallets')
    const FullPalletWeights = (palletsRaw.pallets as any[])
      .filter(Boolean)
      .map(r => ({
        WeightWithPallets: Number(r.WeightWithPallet),  // 👈 map to 'WeightWithPallets'
        Bags: Number(r.Bags)
      }));

    // Slack/Damaged
    const SlackWeights = (palletsRaw.slacks as any[])
      .filter(Boolean)
      .map(r => ({
        GrossWeight: Number(r.GrossWeight),
        Bags: Number(r.Bags)
      }));

    // Spot checks → only include rows with a finite TestWeight
    const TestWeights = (unloadRaw.spotChecks as any[])
      .filter(Boolean)
      .filter(r => r.TestWeight !== null && r.TestWeight !== '' && Number.isFinite(Number(r.TestWeight)))
      .map(r => ({
        TestWeight: Number(r.TestWeight),
        BagNumber: Number(r.BagNumber) as 1 | 2 | 3 | 4 | 5 | 6
      }));

    // Tare weights (3 required rows)
    const TareWeight = (unloadRaw.tareWeights as any[])
      .filter(Boolean)
      .map(r => ({
        TareWeight: Number(r.TareWeight),
        BagNumber: Number(r.BagNumber) as 1 | 2 | 3
      }));

    // Conditions / insects as arrays of literals
    const ConditionOnArrival = this.mapConditionsToArray(this.unloadingFormGroup.get('condition') as FormGroup);
    const InsectActivity = this.mapInsectsToArray(this.unloadingFormGroup.get('insects') as FormGroup);

    // Build final object
    const tally: InboundTallyData = {
      FullPalletWeights,
      SlackWeights,
      TestWeights,

      LocationNumber: unloadRaw.LocationNumber,
      Comments: unloadRaw.Comments ?? '',
      DateStripped: unloadRaw.DateStripped || new Date().toLocaleDateString('en-US'),
      RefPO: `${this.InventoryItem.InventoryID}/${this.InventoryItem.PurchaseOrderID}`,
      CoffeeName: this.InventoryItem.InventoryTypeName,
      Marks: this.InventoryItem.Marks,
      BagsExpected: Number(this.InventoryItem.BagsOrdered),
      ContainerNumber: this.InventoryItem.Container,
      SealNumber: this.InventoryItem.SealNumber,

      ConditionOnArrival,
      InsectActivity,

      PalletWeight: Number(palletsRaw.palletWeight ?? 0),
      TareWeight,

      Weigher: localStorage["displayName"] ?? '',           // you could separate Weigher vs Signature if needed
      Signature: localStorage["displayName"] ?? '',         // re-using — or capture a real signature image string later
      HiLoDriver: unloadRaw.hiloDriver ?? '',
      Helper1: unloadRaw.helper1 ?? '' ,
      Helper2: unloadRaw.helper2 ?? '',
      StackingDriver: unloadRaw.stackingDriver ?? '',
    };

    return tally;
  }

  private hydrateFromData(t: InboundTallyData) {
    // -------- Pallets ----------
    while (this.pallets.length) this.pallets.removeAt(0);
    (t.FullPalletWeights ?? []).forEach(row => {
      const fg = this.createPalletRow();
      fg.patchValue({
        WeightWithPallet: row.WeightWithPallets,
        Bags: row.Bags
      });
      this.pallets.push(fg);
    });
    this.palletFormGroup.get('palletWeight')?.setValue(t.PalletWeight.toString() ?? 0);

    // -------- Slacks ----------
    while (this.slacks.length) this.slacks.removeAt(0);
    (t.SlackWeights ?? []).forEach(row => {
      const fg = this.createSlackRow();
      fg.patchValue({
        GrossWeight: row.GrossWeight,
        Bags: row.Bags
      });
      this.slacks.push(fg);
    });

    // -------- Tare Weights (3) ----------
    while (this.tareWeights.length) this.tareWeights.removeAt(0);
    [1, 2, 3].forEach(n => {
      const fg = this.createTareRow(n);
      const hit = (t.TareWeight ?? []).find(x => x.BagNumber === n);
      fg.get('TareWeight')?.setValue(hit?.TareWeight ?? null);
      this.tareWeights.push(fg);
    });

    // -------- Spot Checks (6) ----------
    while (this.spotChecks.length) this.spotChecks.removeAt(0);
    [1, 2, 3, 4, 5, 6].forEach(n => {
      const fg = this.createSpotRow(n);
      const hit = (t.TestWeights ?? []).find(x => x.BagNumber === n);
      fg.get('TestWeight')?.setValue(hit?.TestWeight ?? null);
      this.spotChecks.push(fg);
    });

    // -------- Crew / meta --------
    this.unloadingFormGroup.patchValue({
      hiloDriver: t.HiLoDriver ?? '',
      helper1: t.Helper1 ?? '',
      helper2: t.Helper2 ?? '',
      stackingDriver: t.StackingDriver ?? '',
      LocationNumber: t.LocationNumber ?? '',
      Comments: t.Comments ?? '',
      DateStripped: t.DateStripped ? String(t.DateStripped).substring(0, 10) : new Date().toISOString().substring(0, 10)
    });

    // -------- Checkboxes ----------
    const cond = {
      condensation: (t.ConditionOnArrival ?? []).includes('Condensation'),
      paperLining: (t.ConditionOnArrival ?? []).includes('Paper Lining'),
      holeInContainer: (t.ConditionOnArrival ?? []).includes('HoleInContainer'),
      other: (t.ConditionOnArrival ?? []).includes('Other')
    };
    (this.unloadingFormGroup.get('condition') as FormGroup).patchValue(cond);

    const insects = {
      larvae: (t.InsectActivity ?? []).includes('Larvae'),
      beetles: (t.InsectActivity ?? []).includes('Beetles'),
      moths: (t.InsectActivity ?? []).includes('Moths'),
      other: (t.InsectActivity ?? []).includes('Other')
    };
    (this.unloadingFormGroup.get('insects') as FormGroup).patchValue(insects);
  }

}
